import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, useAnimation } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';

// Traductions pour la page de contact
const translations = {
  en: {
    discord: {
      title: "Discord",
      handle: "inostudio",
      description: "Chat with us directly"
    },
    wechat: {
      title: "WeChat",
      handle: "Ilyinho",
      description: "Chat with the manager"
    },
    cta: "Click to copy"
  },
  zh: {
    discord: {
      title: "Discord",
      handle: "inostudio",
      description: "加入我们的社区"
    },
    wechat: {
      title: "微信",
      handle: "Ilyinho",
      description: "直接与我们聊天"
    },
    cta: "点击复制"
  }
};

// Container principal - modification pour garder l'espace même sans titres
const ContactContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 60px 5% 40px;
  position: relative;
  overflow: hidden;
  perspective: 1000px;
`;

// Espacement invisible pour remplacer les titres retirés
const TitleSpacing = styled.div`
  height: 135px; /* Ajustez cette valeur selon l'espace occupé par vos anciens titre et sous-titre */
  width: 100%;
`;

// Arrière-plan animé
const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  overflow: hidden;
`;

const Grid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 150%;
  height: 150%;
  background-image: linear-gradient(rgba(30, 30, 30, 0.1) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(30, 30, 30, 0.1) 1px, transparent 1px);
  background-size: 40px 40px;
  transform: perspective(500px) rotateX(60deg) translateZ(-100px) translateY(-20%);
  opacity: 0.5;
`;

const Particles = styled(motion.div)`
  position: absolute;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 10px 2px rgba(255, 255, 255, 0.3);
`;

// Conteneur pour les cartes
const CardsContainer = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 60px;
  width: 100%;
  max-width: 1200px;
  perspective: 1000px;
  margin-top: 10px;
`;

// Carte de contact
const ContactCard = styled(motion.div)`
  position: relative;
  width: 400px;
  height: 500px;
  background: rgba(20, 20, 20, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transform-style: preserve-3d;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.05) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: translateY(-100%);
    transition: transform 0.6s;
    z-index: 0;
  }
  
  &:hover::before {
    transform: translateY(100%);
  }
`;

// Bordure lumineuse animée
const GlowBorder = styled(motion.div)`
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 22px;
  z-index: -1;
  background: ${props => props.color || 'linear-gradient(45deg, #5865F2, #8c9eff)'};
  opacity: 0.7;
  filter: blur(10px);
`;

// Icône de la plateforme
const ContactIcon = styled(motion.div)`
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  position: relative;
  z-index: 1;
  
  svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
  }
`;

// Titre de la plateforme
const ContactTitle = styled(motion.h3)`
  font-size: 2.6rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  z-index: 1;
`;

// Nom d'utilisateur
const ContactHandle = styled(motion.div)`
  font-size: 1.8rem;
  font-weight: 500;
  color: #fff;
  margin-bottom: 25px;
  text-align: center;
  background: ${props => props.color || 'linear-gradient(45deg, #5865F2, #8c9eff)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  z-index: 1;
`;

// Description de la plateforme
const ContactDescription = styled(motion.p)`
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin-bottom: 30px;
  position: relative;
  z-index: 1;
`;

// Bouton de copie
const CopyButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 15px 30px;
  border-radius: 30px;
  font-size: 1.2rem;
  cursor: pointer;
  position: relative;
  z-index: 1;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => props.color || 'linear-gradient(45deg, #5865F2, #8c9eff)'};
    opacity: 0;
    transition: opacity 0.3s;
    z-index: -1;
  }
  
  &:hover::before {
    opacity: 0.8;
  }
`;

// Message de confirmation de copie
const CopyMessage = styled(motion.div)`
  position: fixed;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 12px 25px;
  border-radius: 20px;
  font-size: 1.1rem;
  pointer-events: none;
  z-index: 100;
`;

// Icônes SVG pour les plateformes
const DiscordIcon = () => (
  <svg viewBox="0 0 24 24" fill="#5865F2" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const WeChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="#09B83E" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.296.29.296a.32.32 0 0 0 .171-.054l1.903-1.114a.86.86 0 0 1 .708-.054 10.3 10.3 0 0 0 2.842.396c.187 0 .376-.006.564-.016-.2-.58-.307-1.193-.307-1.825 0-3.724 3.516-6.737 7.84-6.737.215 0 .426.011.637.027C16.395 4.598 12.917 2.188 8.691 2.188zm8.279 5.824c0 .565-.46 1.026-1.025 1.026s-1.026-.46-1.026-1.026.46-1.026 1.026-1.026 1.025.46 1.025 1.026zm4.599 0c0 .565-.46 1.026-1.025 1.026s-1.026-.46-1.026-1.026.46-1.026 1.026-1.026 1.025.46 1.025 1.026zm2.431 7.8c0-3.352-3.319-6.09-7.4-6.09s-7.4 2.738-7.4 6.09 3.319 6.09 7.4 6.09c.86 0 1.68-.133 2.431-.366a.836.836 0 0 1 .659.051l1.717 1.006c.07.039.141.59.211.059.16 0 .29-.133.29-.296 0-.07-.019-.122-.038-.192l-.353-1.335a.581.581 0 0 1 .211-.631c1.6-1.211 2.556-2.958 2.556-4.927h.016v-.049zm-10.388-.498c0 .508-.413.921-.922.921s-.921-.413-.921-.921.413-.921.921-.921.922.413.922.921zm4.599 0c0 .508-.413.921-.921.921s-.922-.413-.922-.921.413-.921.922-.921.921.413.921.921z" />
  </svg>
);

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.3
    }
  }
};

const cardVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1, 
    transition: { 
      type: "spring", 
      damping: 12 
    } 
  },
  hover: { 
    scale: 1.03,
    rotateY: 3,
    rotateX: 3,
    transition: { 
      type: "spring", 
      damping: 10 
    } 
  },
  tap: { 
    scale: 0.98, 
    transition: { 
      type: "spring", 
      damping: 15 
    } 
  }
};

const Contact = () => {
  const { i18n } = useTranslation();
  const [copyMessage, setCopyMessage] = useState({ show: false, text: "" });
  const [particles, setParticles] = useState([]);
  
  const currentLanguage = i18n.language || 'en';
  const content = translations[currentLanguage] || translations.en;
  
  // Génération des particules animées pour l'arrière-plan
  useEffect(() => {
    const newParticles = [];
    for (let i = 0; i < 50; i++) {
      const x = Math.random() * 100;
      const y = Math.random() * 100;
      const size = Math.random() * 3 + 1;
      const duration = Math.random() * 20 + 10;
      const delay = Math.random() * 5;
      newParticles.push({ id: i, x, y, size, duration, delay });
    }
    setParticles(newParticles);
  }, []);

  // Fonction pour copier dans le presse-papiers
  const copyToClipboard = (text, platform) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopyMessage({ 
          show: true, 
          text: `${platform} ID ${currentLanguage === 'zh' ? '已复制!' : 'copied!'}` 
        });
        setTimeout(() => setCopyMessage({ show: false, text: "" }), 2000);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };

  return (
    <PageLayout>
      <ContactContainer>
        {/* Arrière-plan animé */}
        <Background>
          <Grid />
          {particles.map((particle) => (
            <Particles
              key={particle.id}
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`
              }}
              animate={{
                y: ["0%", "100%"],
                opacity: [0, 0.8, 0]
              }}
              transition={{
                duration: particle.duration,
                repeat: Infinity,
                delay: particle.delay,
                ease: "linear"
              }}
            />
          ))}
        </Background>

        {/* Espace invisible pour remplacer les titres retirés */}
        <TitleSpacing />
        
        {/* Cartes de contact */}
        <CardsContainer
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Carte Discord */}
          <ContactCard
            variants={cardVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => copyToClipboard("inostudio", "Discord")}
          >
            <GlowBorder 
              animate={{ 
                opacity: [0.3, 0.7, 0.3], 
                scale: [0.98, 1.01, 0.98] 
              }} 
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              color="linear-gradient(45deg, #5865F2, #8c9eff)"
            />
            
            <ContactIcon
              animate={{ 
                y: [0, -10, 0], 
                rotate: [0, 5, 0, -5, 0] 
              }}
              transition={{ 
                duration: 5, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            >
              <DiscordIcon />
            </ContactIcon>
            
            <ContactTitle>{content.discord.title}</ContactTitle>
            <ContactHandle color="linear-gradient(45deg, #5865F2, #8c9eff)">
              {content.discord.handle}
            </ContactHandle>
            <ContactDescription>{content.discord.description}</ContactDescription>
            
            <CopyButton 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              color="linear-gradient(45deg, #5865F2, #8c9eff)"
            >
              {content.cta}
            </CopyButton>
          </ContactCard>
          
          {/* Carte WeChat */}
          <ContactCard
            variants={cardVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => copyToClipboard("Ilyinho", "WeChat")}
          >
            <GlowBorder 
              animate={{ 
                opacity: [0.3, 0.7, 0.3], 
                scale: [0.98, 1.01, 0.98] 
              }} 
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              color="linear-gradient(45deg, #09B83E, #7dffaa)"
            />
            
            <ContactIcon
              animate={{ 
                y: [0, -10, 0], 
                rotate: [0, 5, 0, -5, 0] 
              }}
              transition={{ 
                duration: 5, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 0.5
              }}
            >
              <WeChatIcon />
            </ContactIcon>
            
            <ContactTitle>{content.wechat.title}</ContactTitle>
            <ContactHandle color="linear-gradient(45deg, #09B83E, #7dffaa)">
              {content.wechat.handle}
            </ContactHandle>
            <ContactDescription>{content.wechat.description}</ContactDescription>
            
            <CopyButton 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              color="linear-gradient(45deg, #09B83E, #7dffaa)"
            >
              {content.cta}
            </CopyButton>
          </ContactCard>
        </CardsContainer>
        
        {/* Message de confirmation de copie */}
        {copyMessage.show && (
          <CopyMessage
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {copyMessage.text}
          </CopyMessage>
        )}
      </ContactContainer>
    </PageLayout>
  );
};

export default Contact;